/**
 * RiskAcceptanceDialog Component
 *
 * Dialog for accepting risk on accessibility issues.
 * Requires a reason and expiration period.
 */

import { useState } from 'react';
import {
  X,
  AlertTriangle,
  Calendar,
  FileText,
  Clock,
  CheckCircle2,
} from 'lucide-react';

export type AcceptancePeriod =
  | 'ONE_WEEK'
  | 'TWO_WEEKS'
  | 'ONE_MONTH'
  | 'THREE_MONTHS'
  | 'SIX_MONTHS'
  | 'ONE_YEAR'
  | 'CUSTOM';

export interface AcceptRiskInput {
  siteId: string;
  issueId: string;
  issueDescription: string;
  reason: string;
  period: AcceptancePeriod;
  customExpiresAt?: string;
  pageUrls?: string[];
  severity?: string;
  category?: string;
  acceptedBy: string;
}

interface RiskAcceptanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: (input: AcceptRiskInput) => Promise<void>;
  issueId: string;
  issueDescription: string;
  severity?: string;
  category?: string;
  pageUrls?: string[];
  siteId: string;
  userName: string;
}

const PERIOD_OPTIONS: { value: AcceptancePeriod; label: string; days: number }[] = [
  { value: 'ONE_WEEK', label: '1 Week', days: 7 },
  { value: 'TWO_WEEKS', label: '2 Weeks', days: 14 },
  { value: 'ONE_MONTH', label: '1 Month', days: 30 },
  { value: 'THREE_MONTHS', label: '3 Months', days: 90 },
  { value: 'SIX_MONTHS', label: '6 Months', days: 180 },
  { value: 'ONE_YEAR', label: '1 Year', days: 365 },
  { value: 'CUSTOM', label: 'Custom Date', days: 0 },
];

const MIN_REASON_LENGTH = 20;

export function RiskAcceptanceDialog({
  isOpen,
  onClose,
  onAccept,
  issueId,
  issueDescription,
  severity,
  category,
  pageUrls,
  siteId,
  userName,
}: RiskAcceptanceDialogProps) {
  const [reason, setReason] = useState('');
  const [period, setPeriod] = useState<AcceptancePeriod>('ONE_MONTH');
  const [customDate, setCustomDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate reason
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Reason must be at least ${MIN_REASON_LENGTH} characters to explain why this risk is acceptable.`);
      return;
    }

    // Validate custom date if selected
    if (period === 'CUSTOM' && !customDate) {
      setError('Please select a custom expiration date.');
      return;
    }

    setIsSubmitting(true);

    try {
      await onAccept({
        siteId,
        issueId,
        issueDescription,
        reason: reason.trim(),
        period,
        customExpiresAt: period === 'CUSTOM' ? new Date(customDate).toISOString() : undefined,
        pageUrls,
        severity,
        category,
        acceptedBy: userName,
      });
      onClose();
      // Reset form
      setReason('');
      setPeriod('ONE_MONTH');
      setCustomDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept risk');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getExpirationDate = () => {
    if (period === 'CUSTOM') {
      return customDate ? new Date(customDate).toLocaleDateString() : 'Select a date';
    }
    const option = PERIOD_OPTIONS.find(o => o.value === period);
    if (!option) return '';
    const date = new Date();
    date.setDate(date.getDate() + option.days);
    return date.toLocaleDateString();
  };

  const minCustomDate = new Date();
  minCustomDate.setDate(minCustomDate.getDate() + 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-light">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-heading-3 font-semibold text-dark-navy">Accept Risk</h2>
              <p className="text-caption text-gray-medium">Acknowledge and temporarily accept this issue</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-light rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-medium" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Issue Info */}
          <div className="bg-gray-light/50 rounded-lg p-3">
            <p className="text-body-sm font-medium text-dark-navy mb-1">Issue</p>
            <p className="text-body-sm text-gray-dark">{issueDescription}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {severity && (
                <span className={`px-2 py-0.5 rounded text-caption font-medium ${
                  severity === 'critical' ? 'bg-red-100 text-red-700' :
                  severity === 'serious' ? 'bg-orange-100 text-orange-700' :
                  severity === 'moderate' ? 'bg-amber-100 text-amber-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {severity}
                </span>
              )}
              {category && (
                <span className="px-2 py-0.5 bg-coral/10 text-coral rounded text-caption">
                  {category}
                </span>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-caption text-amber-800">
              <p className="font-semibold mb-1">Before accepting this risk:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Ensure you have authority to accept this risk</li>
                <li>Consider the impact on users with disabilities</li>
                <li>Document why immediate remediation isn't possible</li>
                <li>Set a review date to revisit this decision</li>
              </ul>
            </div>
          </div>

          {/* Reason Field */}
          <div>
            <label className="block text-body-sm font-medium text-dark-navy mb-2">
              <FileText size={14} className="inline mr-1" />
              Reason for Acceptance <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this risk is being accepted and any mitigating factors..."
              className="w-full px-3 py-2 border border-gray-light rounded-lg text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral resize-none"
              rows={4}
              minLength={MIN_REASON_LENGTH}
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-caption text-gray-medium">
                Minimum {MIN_REASON_LENGTH} characters required
              </p>
              <p className={`text-caption ${reason.length >= MIN_REASON_LENGTH ? 'text-status-success' : 'text-gray-medium'}`}>
                {reason.length}/{MIN_REASON_LENGTH}
              </p>
            </div>
          </div>

          {/* Period Selection */}
          <div>
            <label className="block text-body-sm font-medium text-dark-navy mb-2">
              <Clock size={14} className="inline mr-1" />
              Acceptance Period
            </label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPeriod(option.value)}
                  className={`px-3 py-2 rounded-lg text-body-sm border transition-colors ${
                    period === option.value
                      ? 'bg-coral text-white border-coral'
                      : 'bg-white text-gray-dark border-gray-light hover:border-coral/50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Custom Date Picker */}
            {period === 'CUSTOM' && (
              <div className="mt-2">
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  min={minCustomDate.toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-light rounded-lg text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral"
                  required={period === 'CUSTOM'}
                />
              </div>
            )}

            {/* Expiration Preview */}
            <div className="flex items-center gap-2 mt-2 text-body-sm text-gray-medium">
              <Calendar size={14} />
              <span>Expires: <span className="font-medium text-dark-navy">{getExpirationDate()}</span></span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-body-sm text-red-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-body-sm text-gray-dark hover:bg-gray-light rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || reason.trim().length < MIN_REASON_LENGTH}
              className="flex items-center gap-2 px-4 py-2 bg-coral text-white rounded-lg text-body-sm hover:bg-coral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">
                    <Clock size={16} />
                  </span>
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Accept Risk
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RiskAcceptanceDialog;
